import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Spinner, Text, Alert, AlertIcon } from '@chakra-ui/react';
import { getCheckoutTokenDetails } from '@/api/apis';

const CheckoutDocuments: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            setError('No token provided in URL');
            setLoading(false);
            return;
        }

        fetchDocumentUrl(token);
    }, [searchParams]);

    const fetchDocumentUrl = async (token: string) => {
        try {
            setLoading(true);
            setError(null);

            console.log('Fetching document for token:', token);

            // Call your API using the existing API function
            const response = await getCheckoutTokenDetails({ token });

            if (response.status === 200 && response.data) {
                // Success - redirect to the signed URL
                console.log('Document URL received, redirecting...');
                window.location.href = response?.data;
                // Keep loading state active until redirect happens
                return;
            } else {
                // Handle API errors
                const errorMessage = getErrorMessage(response?.data, response?.status);
                setError(errorMessage);
            }

        } catch (err: any) {
            console.error('API call error:', err);
            setLoading(false);
            // Handle axios error responses
            if (err?.response) {
                const errorMessage = getErrorMessage(err?.response?.data, err?.response?.status);
                setError(errorMessage);
            } else if (err?.request) {
                setError('Network error occurred. Please check your connection and try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            // Only set loading to false if there's an error
            // For successful redirects, keep loading state active
            if (error) {
                setLoading(false);
            }
        }
    };

    const getErrorMessage = (data: any, statusCode: number): string => {
        // Handle different error types based on your API response structure
        if (data?.message) {
            return data.message;
        }

        // Handle specific status codes with user-friendly messages
        switch (statusCode) {
            case 400:
                return 'Invalid input! Please check your access link and try again.';
            case 401:
                return 'Document Link Expired! This link is no longer valid.';
            case 404:
                return 'Document Link Expired! This link is no longer valid.';
            case 500:
                return 'Server error occurred. Please try again later or contact support if the issue persists.';
            default:
                return 'An unexpected error occurred. Please try again or contact support.';
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                p={4}
            >
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text mt={4} fontSize="lg" color="gray.600">
                    Fetching your FFL document, please wait...
                </Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                p={4}
                maxWidth="700px"
                mx="auto"
            >
                <Alert status="error" borderRadius="md" mb={4}>
                    <AlertIcon />
                    <Box>
                        <Text fontSize="lg" fontWeight="semibold">{error}</Text>
                    </Box>
                </Alert>
            </Box>
        );
    }

    // This should rarely be reached since successful responses redirect
    // Keep showing loader for successful cases
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            p={4}
        >
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4} fontSize="lg" color="gray.600">
                Fetching your FFL document, please wait...
            </Text>
        </Box>
    );
};

export default CheckoutDocuments;